import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { TextField } from "@material-ui/core";
import { Formik } from "formik";
import { toast } from "react-toastify";
import ThemeButton from "../../../partials/button/ThemeButton";
import { updatePassword } from "../curd/auth.crud";
import PasswordStrengthIndicator, { validatePasswordStrength } from "./PasswordStrengthIndicator";

const PasswordExpiryModal = ({
  mode = "force", // Only force mode now
  show = false,
  onClose,
  onPasswordChanged,
  onSwitchToForce
}) => {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isForceMode = true; // Always force mode now

  const handlePasswordUpdate = async (values, { setStatus, setSubmitting }) => {
    setLoading(true);
    try {
      await updatePassword(values.currentPassword, values.newPassword);
      toast.success("Password updated successfully!");
      setLoading(false);
      setSubmitting(false);
      
      // Show browser save password dialog
      if (window.navigator.credentials) {
        const passwordCredential = new window.PasswordCredential({
          id: values.username || 'user',
          password: values.newPassword,
        });
        window.navigator.credentials.store(passwordCredential).catch(() => {
          // Browser doesn't support or user declined
        });
      }
      
      // Show success message with browser save instructions
      toast.success("Password updated successfully! 💾 Please save it in your browser when prompted.", {
        autoClose: 5000
      });
      
      if (onPasswordChanged) onPasswordChanged();
      if (onClose) onClose();
    } catch (error) {
      setLoading(false);
      setSubmitting(false);
      const errorMessage = error.response?.data?.message || "Failed to update password";
      setStatus(errorMessage);
      toast.error(errorMessage);
    }
  };

  const validatePasswords = (values) => {
    const errors = {};

    if (!values.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    if (!values.newPassword) {
      errors.newPassword = "New password is required";
    } else {
      // Use strict password validation
      const passwordValidation = validatePasswordStrength(values.newPassword);
      if (!passwordValidation.isValid) {
        errors.newPassword = passwordValidation.errors[0]; // Show first error
      }
    }

    if (!values.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (values.newPassword !== values.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (values.currentPassword && values.newPassword && 
        values.currentPassword === values.newPassword) {
      errors.newPassword = "New password must be different from current password";
    }

    return errors;
  };

  return (
    <Modal
      show={show}
      onHide={undefined}
      backdrop="static"
      keyboard={false}
      className="full-width modal-stretch"
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>
          Secure Password Required
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-4">

        <div>
          <div className="alert alert-danger mb-4">
            <i className="fas fa-shield-alt mr-2"></i>
            Password change required to continue.
          </div>
          <p className="mb-3">
            Your password is over 3 months old and must be updated for security. 
            You cannot access other pages until you create a new secure password.
          </p>
          
         <div className="alert alert-info mb-4" style={{ fontSize: "13px" }}>
            <i className="fas fa-info-circle mr-2"></i>
            <strong>Important:</strong> After updating your password, please save it safely, accept your browser's prompt to update the password, and consider logging out and back in to ensure your browser detects the change and offers to save the new password.
          </div>

            <Formik
              initialValues={{
                currentPassword: "",
                newPassword: "",
                confirmPassword: ""
              }}
              validate={validatePasswords}
              onSubmit={handlePasswordUpdate}
            >
              {({
                values,
                errors,
                touched,
                status,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting
              }) => (
                <form onSubmit={handleSubmit} noValidate>
                  {status && (
                    <div className="alert alert-danger mb-3">
                      {status}
                    </div>
                  )}

                  <div className="form-group mb-3">
                    <div className="position-relative">
                      <TextField
                        type={showCurrentPassword ? "text" : "password"}
                        label="Current Password"
                        name="currentPassword"
                        value={values.currentPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="kt-width-full"
                        margin="normal"
                        error={Boolean(touched.currentPassword && errors.currentPassword)}
                        helperText={touched.currentPassword && errors.currentPassword}
                      />
                      <button
                        type="button"
                        className="btn btn-sm position-absolute"
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "transparent",
                          padding: "0",
                          zIndex: 1
                        }}
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        <i className={`fas ${showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'}`} style={{ color: '#6c757d' }}></i>
                      </button>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <div className="position-relative">
                      <TextField
                        type={showNewPassword ? "text" : "password"}
                        label="New Password"
                        name="newPassword"
                        value={values.newPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="kt-width-full"
                        margin="normal"
                        error={Boolean(touched.newPassword && errors.newPassword)}
                        helperText={touched.newPassword && errors.newPassword}
                      />
                      <button
                        type="button"
                        className="btn btn-sm position-absolute"
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "transparent",
                          padding: "0",
                          zIndex: 1
                        }}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`} style={{ color: '#6c757d' }}></i>
                      </button>
                    </div>
                    {/* Show password strength indicator when user starts typing */}
                    {values.newPassword && (
                      <PasswordStrengthIndicator password={values.newPassword} />
                    )}
                  </div>

                  <div className="form-group mb-4">
                    <div className="position-relative">
                      <TextField
                        type={showConfirmPassword ? "text" : "password"}
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="kt-width-full"
                        margin="normal"
                        error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                        helperText={touched.confirmPassword && errors.confirmPassword}
                      />
                      <button
                        type="button"
                        className="btn btn-sm position-absolute"
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "transparent",
                          padding: "0",
                          zIndex: 1
                        }}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} style={{ color: '#6c757d' }}></i>
                      </button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <ThemeButton
                      type="submit"
                      className="brand-button w-100"
                      disabled={
                        isSubmitting || 
                        loading || 
                        !validatePasswordStrength(values.newPassword).isValid ||
                        values.newPassword !== values.confirmPassword ||
                        !values.currentPassword
                      }
                    >
                      {loading ? "Updating..." : "Update Password"}
                    </ThemeButton>
                  </div>
                </form>
              )}
            </Formik>
          </div>
      </Modal.Body>
    </Modal>
  );
};

export default PasswordExpiryModal;
